import { parseAccountIdsFromString } from '@/helpers/parseAccountIdsFromString';
import { createAccountIdsValidator, validateAccountIds } from '@/helpers/validateAccountIds';
import { useRunTaskMutation } from '@/services/adminTasks';
import { Box, Button, FormikTextareaInput, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { object, Schema, string } from 'yup';

import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';

import { BulkModifyAccountsResults, BulkModifyAccountsResultsData } from './BulkModifyAccountsResults';

const MAX_ACCOUNT_IDS = 50;

interface BulkEnrollDomesticContentFormData {
  accountIds: string;
}

const bulkEnrollDomesticContentSchema = {
  accountIds: string()
    .required('Account IDs are required')
    .test('is-valid-mongo-ids', 'Invalid account IDs', createAccountIdsValidator())
    .test('max-account-ids', `Maximum of ${MAX_ACCOUNT_IDS} account IDs allowed`, async (value) => {
      if (!value) return true;
      const result = await validateAccountIds(value);
      if (!result.isValid || !result.accountIds) return true;
      return result.accountIds.length <= MAX_ACCOUNT_IDS;
    }),
};

export const BulkEnrollDomesticContentFormSchema: Schema<BulkEnrollDomesticContentFormData> = object()
  .shape(bulkEnrollDomesticContentSchema)
  .noUnknown(true);

export function BulkEnrollDomesticContent() {
  const [results, setResults] = useState<BulkModifyAccountsResultsData>();
  const [runTask] = useRunTaskMutation();

  const handleSubmit = async (values: BulkEnrollDomesticContentFormData, { setSubmitting }: any) => {
    const loadingToastId = toast.loading('Enrolling accounts in domestic content...');
    try {
      let accountIds = await parseAccountIdsFromString(values.accountIds);
      accountIds = accountIds.filter((id: string) => id.trim().length > 0);

      const enrollResults = await runTask({
        task: 'bulkEnrollDomesticContent',
        accountIds,
      }).unwrap();

      setResults(enrollResults);
    } catch (e) {
      console.error(e);
      toast.error('Error enrolling accounts in domestic content');
    } finally {
      toast.dismiss(loadingToastId);
      setSubmitting(false);
    }
  };

  const initialValues: BulkEnrollDomesticContentFormData = {
    accountIds: '',
  };

  const handleCloseResults = () => {
    setResults(undefined);
  };

  return (
    <>
      <Helmet>
        <title>Bulk Enroll Domestic Content</title>
      </Helmet>
      <MainContainer>
        {!results ? (
          <Formik
            validationSchema={BulkEnrollDomesticContentFormSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, submitForm }) => (
              <Form>
                <Box>
                  <Box>
                    <FormSection
                      title="Account IDs"
                      description={
                        <Box gap="sm">
                          <Box>
                            {`Enter account IDs separated by commas or one per line (max ${MAX_ACCOUNT_IDS})`}.
                          </Box>
                          <Box>
                            If an account is already enrolled in domestic content, it will be updated to the latest
                            modifier pricing.
                          </Box>
                        </Box>
                      }
                    >
                      <Field
                        name="accountIds"
                        id="accountIds"
                        component={FormikTextareaInput}
                        isDisabled={isSubmitting}
                        rows={10}
                      />
                    </FormSection>
                  </Box>
                </Box>
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-end',
                  }}
                  justifyContent={{
                    tablet: 'flex-end',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding="lg 0 0 0"
                >
                  <Button size="md" variant="primary" type="submit" onClick={submitForm} isLoading={isSubmitting}>
                    Enroll in Domestic Content
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <BulkModifyAccountsResults results={results} closeResults={handleCloseResults} />
        )}
      </MainContainer>
    </>
  );
}
